import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getFieldVisits, getFieldTasks, getMasterData } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft,
  MapPin,
  Building,
  Calendar,
  Users,
  TrendingUp,
  Clock,
  AlertTriangle,
  ChevronRight,
  ChevronLeft,
  Eye,
  Plus,
  BarChart3,
  CalendarDays
} from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { MasterDataAutocomplete } from '../components/MasterDataAutocomplete';
import { useScreenState, useScrollRestoration } from '../hooks/useScreenState';

// Calendar helper functions
const getDaysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
const getFirstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();
const formatMonthYear = (year, month) => {
  const date = new Date(year, month, 1);
  return date.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

export default function FieldActivityReports() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [visits, setVisits] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [buyerMarkets, setBuyerMarkets] = useState([]);
  const [selectedMarket, setSelectedMarket] = useScreenState('fieldActivityReports.selectedMarket', 'all');
  const [activeTab, setActiveTab] = useScreenState('fieldActivityReports.activeTab', 'market-report');
  
  // Calendar state
  const [currentDate, setCurrentDate] = useScreenState('fieldActivityReports.currentDate', new Date());
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();

  useScrollRestoration('fieldActivityReports', !loading);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [visitsRes, tasksRes, marketsRes] = await Promise.all([
        getFieldVisits(),
        getFieldTasks(),
        getMasterData('buyer_market')
      ]);
      setVisits(visitsRes.data);
      setTasks(tasksRes.data);
      setBuyerMarkets(marketsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Filter visits by selected market
  const filteredVisits = useMemo(() => {
    if (selectedMarket === 'all') return visits;
    return visits.filter(v => v.geography_market === selectedMarket);
  }, [visits, selectedMarket]);

  // Group visits by market for report
  const marketStats = useMemo(() => {
    const stats = {};
    
    // Initialize all markets
    buyerMarkets.forEach(market => {
      stats[market.name] = {
        name: market.name,
        totalVisits: 0,
        thisMonthVisits: 0,
        lastVisitDate: null,
        uniqueBuyers: new Set(),
        pendingTasks: 0
      };
    });
    
    // Add 'Unknown' for visits without market
    stats['Unknown'] = {
      name: 'Unknown',
      totalVisits: 0,
      thisMonthVisits: 0,
      lastVisitDate: null,
      uniqueBuyers: new Set(),
      pendingTasks: 0
    };
    
    const now = new Date();
    const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    
    visits.forEach(visit => {
      const marketName = visit.geography_market || 'Unknown';
      if (!stats[marketName]) {
        stats[marketName] = {
          name: marketName,
          totalVisits: 0,
          thisMonthVisits: 0,
          lastVisitDate: null,
          uniqueBuyers: new Set(),
          pendingTasks: 0
        };
      }
      
      stats[marketName].totalVisits++;
      stats[marketName].uniqueBuyers.add(visit.buyer_id);
      
      const visitDate = new Date(visit.visit_date);
      if (visitDate >= firstOfMonth) {
        stats[marketName].thisMonthVisits++;
      }
      
      if (!stats[marketName].lastVisitDate || visitDate > new Date(stats[marketName].lastVisitDate)) {
        stats[marketName].lastVisitDate = visit.visit_date;
      }
    });
    
    // Count pending tasks per market
    tasks.forEach(task => {
      if (task.status !== 'Completed') {
        const visit = visits.find(v => v.id === task.visit_id);
        if (visit) {
          const marketName = visit.geography_market || 'Unknown';
          if (stats[marketName]) {
            stats[marketName].pendingTasks++;
          }
        }
      }
    });
    
    // Convert Set to count and filter out empty markets
    return Object.values(stats)
      .map(s => ({ ...s, uniqueBuyers: s.uniqueBuyers.size }))
      .filter(s => s.totalVisits > 0 || buyerMarkets.some(m => m.name === s.name))
      .sort((a, b) => b.totalVisits - a.totalVisits);
  }, [visits, tasks, buyerMarkets]);

  // Calendar data - visits by date
  const visitsByDate = useMemo(() => {
    const byDate = {};
    filteredVisits.forEach(visit => {
      const date = visit.visit_date;
      if (!byDate[date]) {
        byDate[date] = [];
      }
      byDate[date].push(visit);
    });
    return byDate;
  }, [filteredVisits]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const getDaysSinceLastVisit = (lastVisitDate) => {
    if (!lastVisitDate) return null;
    const now = new Date();
    const last = new Date(lastVisitDate);
    const diffTime = Math.abs(now - last);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getMarketHealthColor = (daysSince) => {
    if (daysSince === null) return 'text-gray-400';
    if (daysSince <= 7) return 'text-green-600';
    if (daysSince <= 14) return 'text-yellow-600';
    if (daysSince <= 30) return 'text-orange-600';
    return 'text-red-600';
  };

  // Calendar navigation
  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    const days = [];
    
    // Add empty cells for days before the first day
    for (let i = 0; i < firstDay; i++) {
      days.push({ day: null, date: null });
    }
    
    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      days.push({
        day,
        date: dateStr,
        visits: visitsByDate[dateStr] || [],
        isToday: dateStr === new Date().toISOString().split('T')[0]
      });
    }
    
    return days;
  }, [currentYear, currentMonth, visitsByDate]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-maroon-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="field-activity-reports">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/field-activity')}
            data-testid="back-btn"
          >
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-xl font-bold text-maroon-900">Field Activity Reports</h1>
            <p className="text-sm text-gray-500">Market analysis and visit calendar</p>
          </div>
        </div>
        <Button
          size="sm"
          className="bg-maroon-600 hover:bg-maroon-700"
          onClick={() => navigate('/field-activity/new-visit')}
        >
          <Plus className="h-4 w-4 mr-1" />
          New Visit
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="market-report" className="flex items-center gap-2">
            <BarChart3 className="h-4 w-4" />
            Market Report
          </TabsTrigger>
          <TabsTrigger value="calendar" className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            Calendar
          </TabsTrigger>
        </TabsList>

        {/* Market Report Tab */}
        <TabsContent value="market-report" className="space-y-4">
          {/* Summary Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Card className="border-l-4 border-l-maroon-500">
              <CardContent className="p-3 lg:p-2">
                <div className="flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-maroon-600" />
                  <div>
                    <p className="text-xs text-gray-500">Markets Covered</p>
                    <p className="text-lg lg:text-xl font-bold text-maroon-900">
                      {marketStats.filter(m => m.totalVisits > 0).length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500">
              <CardContent className="p-3 lg:p-2">
                <div className="flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-xs text-gray-500">Total Visits</p>
                    <p className="text-lg lg:text-xl font-bold text-blue-900">{visits.length}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500">
              <CardContent className="p-3 lg:p-2">
                <div className="flex items-center gap-2">
                  <Building className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-xs text-gray-500">Buyers Visited</p>
                    <p className="text-lg lg:text-xl font-bold text-green-900">
                      {new Set(visits.map(v => v.buyer_id)).size}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="p-3 lg:p-2">
                <div className="flex items-center gap-2">
                  <Clock className="h-5 w-5 text-yellow-600" />
                  <div>
                    <p className="text-xs text-gray-500">Pending Tasks</p>
                    <p className="text-lg lg:text-xl font-bold text-yellow-900">
                      {tasks.filter(t => t.status !== 'Completed').length}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Market-wise Report */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base lg:text-lg flex items-center gap-2">
                <MapPin className="h-5 w-5 text-maroon-600" />
                Market-wise Analysis
              </CardTitle>
            </CardHeader>
            <CardContent>
              {marketStats.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <MapPin className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                  <p>No market data available</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {/* Header */}
                  <div className="hidden sm:grid sm:grid-cols-6 gap-2 px-3 py-2 text-xs font-medium text-gray-500 border-b">
                    <div className="col-span-2">Market</div>
                    <div className="text-center">Visits</div>
                    <div className="text-center">This Month</div>
                    <div className="text-center">Buyers</div>
                    <div className="text-right">Last Visit</div>
                  </div>
                  
                  {marketStats.map((market) => {
                    const daysSince = getDaysSinceLastVisit(market.lastVisitDate);
                    const healthColor = getMarketHealthColor(daysSince);
                    
                    return (
                      <div
                        key={market.name}
                        className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => {
                          setSelectedMarket(market.name);
                          setActiveTab('calendar');
                        }}
                      >
                        {/* Mobile View */}
                        <div className="sm:hidden">
                          <div className="flex items-center justify-between mb-2">
                            <span className="font-medium text-maroon-900">{market.name}</span>
                            <Badge className={`text-xs ${healthColor} bg-transparent`}>
                              {daysSince !== null ? `${daysSince}d ago` : 'No visits'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500">
                            <span>{market.totalVisits} visits</span>
                            <span>{market.thisMonthVisits} this month</span>
                            <span>{market.uniqueBuyers} buyers</span>
                            {market.pendingTasks > 0 && (
                              <span className="text-yellow-600">{market.pendingTasks} pending</span>
                            )}
                          </div>
                        </div>
                        
                        {/* Desktop View */}
                        <div className="hidden sm:grid sm:grid-cols-6 gap-2 items-center">
                          <div className="col-span-2 flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-maroon-500" />
                            <span className="font-medium text-maroon-900">{market.name}</span>
                            {market.pendingTasks > 0 && (
                              <Badge className="text-xs bg-yellow-100 text-yellow-800">
                                {market.pendingTasks} tasks
                              </Badge>
                            )}
                          </div>
                          <div className="text-center font-semibold">{market.totalVisits}</div>
                          <div className="text-center">
                            <Badge className={market.thisMonthVisits > 0 ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                              {market.thisMonthVisits}
                            </Badge>
                          </div>
                          <div className="text-center">{market.uniqueBuyers}</div>
                          <div className={`text-right text-sm ${healthColor}`}>
                            {formatDate(market.lastVisitDate)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Markets needing attention */}
          {marketStats.filter(m => getDaysSinceLastVisit(m.lastVisitDate) > 14 || m.lastVisitDate === null).length > 0 && (
            <Card className="border-orange-200">
              <CardHeader className="pb-2 bg-orange-50 rounded-t-lg">
                <CardTitle className="text-base lg:text-lg flex items-center gap-2 text-orange-800">
                  <AlertTriangle className="h-5 w-5" />
                  Markets Needing Attention
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-3">
                <div className="flex flex-wrap gap-2">
                  {marketStats
                    .filter(m => getDaysSinceLastVisit(m.lastVisitDate) > 14 || m.lastVisitDate === null)
                    .map((market) => (
                      <Badge
                        key={market.name}
                        className="bg-orange-100 text-orange-800 cursor-pointer hover:bg-orange-200"
                        onClick={() => {
                          setSelectedMarket(market.name);
                          setActiveTab('calendar');
                        }}
                      >
                        {market.name}
                        {market.lastVisitDate && (
                          <span className="ml-1 text-orange-600">
                            ({getDaysSinceLastVisit(market.lastVisitDate)}d)
                          </span>
                        )}
                      </Badge>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Calendar Tab */}
        <TabsContent value="calendar" className="space-y-4">
          {/* Calendar Header */}
          <Card>
            <CardContent className="p-3 lg:p-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={goToPreviousMonth}>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <h2 className="text-lg font-semibold text-maroon-900 min-w-[180px] text-center">
                    {formatMonthYear(currentYear, currentMonth)}
                  </h2>
                  <Button variant="ghost" size="sm" onClick={goToNextMonth}>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={goToToday}>
                    Today
                  </Button>
                </div>
                <MasterDataAutocomplete
                  value={selectedMarket}
                  onValueChange={setSelectedMarket}
                  options={[
                    { value: 'all', label: 'All Markets', pinned: true },
                    ...buyerMarkets.map((market) => ({ value: market.name, label: market.name })),
                  ]}
                  placeholder="All Markets"
                  searchPlaceholder="Search markets..."
                  className="w-full sm:w-48"
                  testId="reports-market-filter"
                />
              </div>
            </CardContent>
          </Card>

          {/* Calendar Grid */}
          <Card>
            <CardContent className="p-3 lg:p-2">
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-1 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                  <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((dayData, index) => (
                  <div
                    key={index}
                    className={`min-h-[80px] sm:min-h-[100px] p-1 border rounded-lg ${
                      dayData.day === null
                        ? 'bg-gray-50 border-transparent'
                        : dayData.isToday
                        ? 'border-maroon-500 bg-maroon-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    {dayData.day !== null && (
                      <>
                        <div className={`text-sm font-medium mb-1 ${dayData.isToday ? 'text-maroon-600' : 'text-gray-700'}`}>
                          {dayData.day}
                        </div>
                        <div className="space-y-1">
                          {dayData.visits.slice(0, 2).map((visit, vIndex) => (
                            <div
                              key={vIndex}
                              className="text-xs p-1 bg-blue-100 text-blue-800 rounded truncate cursor-pointer hover:bg-blue-200"
                              onClick={() => navigate(`/field-activity/visit/${visit.id}`)}
                              title={`${visit.buyer_name} - ${visit.geography_market}`}
                            >
                              <span className="hidden sm:inline">{visit.buyer_name}</span>
                              <span className="sm:hidden">{visit.buyer_name.substring(0, 8)}</span>
                            </div>
                          ))}
                          {dayData.visits.length > 2 && (
                            <div className="text-xs text-gray-500 text-center">
                              +{dayData.visits.length - 2} more
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Visit List for selected day/market */}
          {selectedMarket !== 'all' && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base lg:text-lg flex items-center gap-2">
                  <Building className="h-5 w-5 text-maroon-600" />
                  {selectedMarket} Visits ({filteredVisits.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {filteredVisits.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <Calendar className="h-10 w-10 mx-auto mb-2 text-gray-300" />
                    <p>No visits in {selectedMarket}</p>
                    <Button
                      size="sm"
                      className="mt-3 bg-maroon-600 hover:bg-maroon-700"
                      onClick={() => navigate('/field-activity/new-visit')}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Plan a Visit
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {filteredVisits.slice(0, 10).map((visit) => (
                      <div
                        key={visit.id}
                        className="flex items-center justify-between p-2 bg-gray-50 rounded-lg hover:bg-gray-100 cursor-pointer"
                        onClick={() => navigate(`/field-activity/visit/${visit.id}`)}
                      >
                        <div>
                          <p className="font-medium text-sm">{visit.buyer_name}</p>
                          <p className="text-xs text-gray-500 flex items-center gap-2">
                            <Calendar className="h-3 w-3" />
                            {formatDate(visit.visit_date)}
                            {visit.staff_names?.length > 0 && (
                              <>
                                <Users className="h-3 w-3 ml-2" />
                                {visit.staff_names.join(', ')}
                              </>
                            )}
                          </p>
                        </div>
                        <Eye className="h-4 w-4 text-gray-400" />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
